import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getBranchById, createBranch, updateBranch, getCountries, getZones, getCities, getCurrencies, getUsers } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import FloatingInput from "../components/UI/FloatingInput";
import SelectBox from "../components/UI/SelectBox";
import ToggleSwitch from "../components/UI/ToggleSwitch";
import { notify } from "../utils/notify";
import PageHeader from "../components/UI/PageHeader";
import Select from "react-select";
import { useTranslation } from "../context/I18nContext";

// Matches the react-select styling AddEditGroup.js already uses for its
// Permissions picker, so a multi-select list looks consistent everywhere.
const customSelectStyles = {
  control: (base) => ({
    ...base,
    padding: "2px",
    borderRadius: "12px",
    borderColor: "#f3f4f6",
    backgroundColor: "#f9fafb80",
    "&:hover": { borderColor: "var(--primary-color)" },
    boxShadow: "none",
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "var(--primary-color)",
    color: "white",
    borderRadius: "8px",
  }),
  multiValueLabel: (base) => ({ ...base, color: "white" }),
};

export default function AddEditBranch() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const { t } = useTranslation("branches");
  const { t: tCommon } = useTranslation("common");

  // BUSINESS_TYPE_OPTIONS needs t(), so it's built here rather than as a
  // module-level constant (a module-level array has no hook access).
  const BUSINESS_TYPE_OPTIONS = [
    { value: "retail", label: t("business_type_retail", "Retail") },
    { value: "restaurant", label: t("business_type_restaurant_option", "Restaurant / Café") },
    { value: "hybrid", label: t("business_type_hybrid_option", "Hybrid (both)") },
    // salon/beauty_center are the first business types that actually GATE a
    // feature — see ServiceCapableBusinessTypes in branch_handler.go — a
    // branch set to either of these is what makes Checkout.js's "Sell
    // Service" tab show up for that branch's shift.
    { value: "salon", label: t("business_type_salon_option", "Salon") },
    { value: "beauty_center", label: t("business_type_beauty_center_option", "Beauty Center") },
  ];

  const [countries, setCountries] = useState([]);
  const [zones, setZones] = useState([]);
  const [cities, setCities] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    name: "",
    business_type: "retail",
    country_id: "",
    zone_id: "",
    city_id: "",
    currency_id: "",
    address: "",
    phone: "",
    is_active: true,
    restrict_held_orders_to_cashier: false,
    auto_print_receipt: true,
    manager_id: "",
  });
  // assignedUsers is react-select's own {value,label} shape — kept separate
  // from `form` since it isn't a plain scalar field like everything else.
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    getCountries(token).then(setCountries).catch(() => notify.error(t("notify_load_countries_failed", "Failed to load countries")));
    getCurrencies(token).then(setCurrencies).catch(() => notify.error(t("notify_load_currencies_failed", "Failed to load currencies")));
    // limit is set high like the Cities load below — otherwise the
    // backend's default pagination page size silently truncates this to
    // its first ~10-20 users instead of everyone available to assign.
    getUsers(token, { limit: 1000 }).then((res) => setUsers(res?.data || res || [])).catch(() => notify.error(t("notify_load_users_failed", "Failed to load users")));

    if (id) {
      getBranchById(token, id).then((b) => {
        setForm({
          name: b.name || "",
          business_type: b.business_type || "retail",
          country_id: b.country_id ? String(b.country_id) : "",
          zone_id: b.zone_id ? String(b.zone_id) : "",
          city_id: b.city_id ? String(b.city_id) : "",
          currency_id: b.currency_id ? String(b.currency_id) : "",
          address: b.address || "",
          phone: b.phone || "",
          is_active: b.is_active !== undefined ? b.is_active : true,
          restrict_held_orders_to_cashier: b.restrict_held_orders_to_cashier || false,
          auto_print_receipt: b.auto_print_receipt !== undefined ? b.auto_print_receipt : true,
          manager_id: b.manager_id ? String(b.manager_id) : "",
        });
        setAssignedUsers((b.assigned_users || []).map((u) => ({ value: u.id, label: u.username || u.email })));
      }).catch(() => notify.error(t("notify_load_branch_failed", "Failed to load branch")));
    }
  }, [id, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (form.country_id) {
      getZones(token, { country_id: form.country_id }).then(setZones).catch(() => setZones([]));
    } else {
      setZones([]);
    }
  }, [form.country_id, token]);

  useEffect(() => {
    // Cities are server-paginated (there are ~230k of them), so this must
    // both scope to the narrowest filter available (zone, falling back to
    // country) AND ask for a high limit explicitly — otherwise the backend's
    // default page size silently truncates the dropdown to its first ~10-20
    // results instead of every city that actually matches.
    if (form.zone_id) {
      getCities(token, { zone_id: form.zone_id, limit: 1000 }).then((res) => setCities(res?.data || res || [])).catch(() => setCities([]));
    } else if (form.country_id) {
      getCities(token, { country_id: form.country_id, limit: 1000 }).then((res) => setCities(res?.data || res || [])).catch(() => setCities([]));
    } else {
      setCities([]);
    }
  }, [form.country_id, form.zone_id, token]);

  const countryOptions = countries.map((c) => ({ value: String(c.id), label: c.name }));
  const zoneOptions = [{ value: "", label: t("zone_none_option", "No zone") }, ...zones.map((z) => ({ value: String(z.id), label: z.name }))];
  const cityOptions = [{ value: "", label: t("city_none_option", "No city") }, ...cities.map((c) => ({ value: String(c.id), label: c.name }))];
  // Leaving this unset means "use the base currency" — see docs/currency-design.md.
  const currencyOptions = [
    { value: "", label: t("currency_use_base_option", "Use base currency") },
    ...currencies.map((c) => ({ value: String(c.id), label: `${c.name} (${c.code})${c.is_base ? t("currency_base_suffix", " — Base") : ""}` })),
  ];
  const userSelectOptions = users.map((u) => ({ value: u.id, label: u.username || u.email }));
  // The manager doesn't have to already be in the assigned-users list —
  // e.g. a regional manager who oversees this branch without personally
  // ringing up sales here — so this stays the full Users list, not just
  // assignedUsers.
  const managerOptions = [{ value: "", label: t("manager_none_option", "No manager assigned") }, ...users.map((u) => ({ value: String(u.id), label: u.username || u.email }))];

  const setField = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = async () => {
    setErrors({});
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = t("validation_name_required", "Branch name is required");

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      notify.error(tCommon("validation_fill_required_fields", "Please fill in the required fields correctly"));
      return;
    }

    setLoading(true);
    const payload = {
      name: form.name.trim(),
      business_type: form.business_type,
      country_id: form.country_id ? Number(form.country_id) : 0,
      zone_id: form.zone_id ? Number(form.zone_id) : 0,
      city_id: form.city_id ? Number(form.city_id) : 0,
      currency_id: form.currency_id ? Number(form.currency_id) : 0,
      address: form.address.trim(),
      phone: form.phone.trim(),
      is_active: form.is_active,
      restrict_held_orders_to_cashier: form.restrict_held_orders_to_cashier,
      auto_print_receipt: form.auto_print_receipt,
      manager_id: form.manager_id ? Number(form.manager_id) : 0,
      user_ids: assignedUsers.map((u) => u.value),
    };

    try {
      if (id) {
        await updateBranch(token, id, payload);
        notify.success(t("notify_updated", "Branch updated successfully"));
      } else {
        await createBranch(token, payload);
        notify.success(t("notify_created", "Branch created successfully"));
      }
      navigate("/branches");
    } catch (err) {
      notify.error(err.data?.error || err.message || tCommon("error_generic", "Something went wrong"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto">
      <PageHeader className="flex items-center gap-4 mb-8 border-b border-gray-100 pb-6">
        <button onClick={() => navigate("/branches")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
          <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
        </button>
        <div>
          <h2 className="text-2xl font-black text-gray-800 tracking-tight">{id ? t("edit_title", "Edit Branch") : t("add_title", "Add New Branch")}</h2>
          <p className="text-gray-400 text-sm italic font-medium">{t("form_subtitle", "Business type decides which POS features this branch sees")}</p>
        </div>
      </PageHeader>

      <div className="bg-white p-6 md:p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
        <div className="space-y-6">
          <FloatingInput label={t("field_name_label", "Branch Name")} value={form.name} onChange={setField("name")} error={errors.name} icon={ICONS.Branches} />

          <SelectBox
            label={t("field_business_type_label", "Business Type")}
            options={BUSINESS_TYPE_OPTIONS}
            value={form.business_type}
            onChange={(val) => setForm((prev) => ({ ...prev, business_type: val }))}
          />

          <SelectBox
            label={t("field_country_label", "Country (optional)")}
            options={countryOptions}
            value={form.country_id}
            onChange={(val) => setForm((prev) => ({ ...prev, country_id: val, zone_id: "", city_id: "" }))}
          />

          <div className="grid grid-cols-2 gap-4">
            <SelectBox
              label={t("field_zone_label", "Zone (optional)")}
              options={zoneOptions}
              value={form.zone_id}
              onChange={(val) => setForm((prev) => ({ ...prev, zone_id: val, city_id: "" }))}
            />
            <SelectBox
              label={t("field_city_label", "City (optional)")}
              options={cityOptions}
              value={form.city_id}
              onChange={(val) => setForm((prev) => ({ ...prev, city_id: val }))}
            />
          </div>

          <SelectBox
            label={t("field_currency_label", "Currency (optional)")}
            options={currencyOptions}
            value={form.currency_id}
            onChange={(val) => setForm((prev) => ({ ...prev, currency_id: val }))}
          />

          <FloatingInput label={t("field_address_label", "Address")} value={form.address} onChange={setField("address")} />
          <FloatingInput label={t("field_phone_label", "Phone")} value={form.phone} onChange={setField("phone")} />

          <ToggleSwitch
            label={tCommon("status_active", "Active")}
            checked={form.is_active}
            onChange={(val) => setForm((prev) => ({ ...prev, is_active: val }))}
          />
          <ToggleSwitch
            label={t("toggle_restrict_held_orders", "Restrict held orders to the cashier who held them")}
            checked={form.restrict_held_orders_to_cashier}
            onChange={(val) => setForm((prev) => ({ ...prev, restrict_held_orders_to_cashier: val }))}
          />
          <p className="text-[11px] text-gray-400 -mt-4">
            {t("restrict_held_orders_hint", "Off (default): any cashier at this branch can resume a colleague's parked cart. On: only the cashier who held it can.")}
          </p>
          <ToggleSwitch
            label={t("toggle_auto_print_receipt", "Automatically print a receipt when a sale completes")}
            checked={form.auto_print_receipt}
            onChange={(val) => setForm((prev) => ({ ...prev, auto_print_receipt: val }))}
          />
          <p className="text-[11px] text-gray-400 -mt-4">
            {t("auto_print_receipt_hint", "On (default): every completed sale at this branch auto-prints to its default printer, online or once an offline sale syncs. Off: receipts only print when a cashier clicks Print Receipt.")}
          </p>

          <div className="space-y-3 pt-2 border-t border-gray-50">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ms-4 block pt-4">
              {t("assigned_users_label", "Users Assigned to This Branch")}
            </label>
            <Select
              options={userSelectOptions}
              value={assignedUsers}
              onChange={setAssignedUsers}
              isMulti
              placeholder={t("assigned_users_placeholder", "Select which users can work at this branch...")}
              styles={customSelectStyles}
              className="text-sm"
            />
            <p className="text-[11px] text-gray-400 ms-4 italic">
              {t("assigned_users_hint", "* Only users listed here can open a shift or see anything (orders, shifts, inventory) tied to this branch — unless they hold the \"All Branches Access\" permission, which sees every branch regardless.")}
            </p>
          </div>

          <SelectBox
            label={t("field_manager_label", "Branch Manager (optional)")}
            options={managerOptions}
            value={form.manager_id}
            onChange={(val) => setForm((prev) => ({ ...prev, manager_id: val }))}
          />
          <p className="text-[11px] text-gray-400 -mt-4">
            {t("manager_hint", "The manager can cancel any held order at this branch, even one a different cashier started.")}
          </p>

          <div className="flex gap-4 pt-6 border-t border-gray-50">
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={loading}
              className="flex-grow shadow-lg shadow-blue-500/10"
              icon={id ? ICONS.Save : ICONS.Plus}
            >
              {id ? t("btn_update_branch", "Update Branch") : t("btn_create_branch", "Create Branch")}
            </Button>
            <Button variant="secondary" onClick={() => navigate("/branches")}>{tCommon("btn_cancel", "Cancel")}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
